import React, { useState, useEffect } from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import "../../Styles/Faculty.css";
import teacher from "../../assets/teacher.png";
import books from "../../assets/books.png";
import expired from "../../assets/expired.png";
import axios from "axios";
import Loader from "../../Components/Loader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Home() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const formattedTime = currentTime.toLocaleString();
  const [studentData, setStudentData] = useState({
    subjectCount: 0,
    classCount: 0,
    name: "",
  });
  const [attendanceData, setAttendanceData] = useState({
    present: 0,
    absent: 0,
    total: 0,
  });
  const [subjectAttendanceData, setSubjectAttendanceData] = useState([]);
  const [yearlyAttendanceData, setYearlyAttendanceData] = useState(
    Array.from({ length: 12 }, () => ({
      present: 0,
      absent: 0,
    }))
  );
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);
  // Fetch announcements
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          "https://api.uniums.com/api/announcement/view-announcements-for-user",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setAnnouncements(response.data.data);
      } catch (error) {
        console.error("Error fetching announcements:", error);
        toast.error("Failed to fetch announcements.");
      }
    };

    fetchAnnouncements();
  }, []);

  // Fetch student basic data
  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const response = await axios.get(
          "https://api.uniums.com/api/student/student-dashboard",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStudentData(response.data.data);
      } catch (error) {
        console.error("Error fetching student data:", error);
        toast.error("Failed to fetch student data.");
      }
    };
    fetchStudentData();
  }, []);

  // Fetch attendance data for the selected month
  useEffect(() => {
    const fetchAttendanceData = async () => {
      setMonthLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          `https://api.uniums.com/api/student/student-attendance?month=${selectedMonth}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data && response.data.data) {
          const data = response.data.data;

          // Calculate overall attendance for the month
          const overallPresent = data.reduce(
            (acc, subject) => acc + subject.present,
            0
          );
          const overallAbsent = data.reduce(
            (acc, subject) => acc + subject.absent,
            0
          );

          setAttendanceData({
            present: overallPresent,
            absent: overallAbsent,
            total: overallPresent + overallAbsent,
          });

          // Process subject attendance data and add percentage
          const processedData = data.map((subject) => {
            const total = subject.present + subject.absent;
            const percentage =
              total > 0 ? Math.round((subject.present / total) * 100) : 0;

            return {
              ...subject,
              attendancePercentage: percentage,
              // Since the API doesn't provide the latest date, we'll use the current date
              // In a real app, you would want the API to provide this information
              latestDate: new Date().toISOString(),
            };
          });

          setSubjectAttendanceData(processedData);

          // Update the yearly data for the selected month
          setYearlyAttendanceData((prev) => {
            const newData = [...prev];
            newData[selectedMonth] = {
              present: overallPresent,
              absent: overallAbsent,
            };
            return newData;
          });
        }
      } catch (error) {
        console.error("Error fetching attendance data:", error);
        toast.error("Failed to fetch attendance data.");
      } finally {
        setMonthLoading(false);
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, [selectedMonth]);

  // Fetch yearly attendance data (this would ideally be a separate API call)
  useEffect(() => {
    const fetchYearlyAttendanceData = async () => {
      try {
        const token = localStorage.getItem("authToken");

        // Ideally, you would have an API endpoint that returns yearly data
        // For now, we'll simulate it by making 12 separate calls
        const yearlyData = Array.from({ length: 12 }, () => ({
          present: 0,
          absent: 0,
        }));

        // In a real implementation, you would have a dedicated API endpoint for this
        // This is just a placeholder to show the structure
        setYearlyAttendanceData(yearlyData);
      } catch (error) {
        console.error("Error fetching yearly attendance data:", error);
      }
    };

    fetchYearlyAttendanceData();
  }, []);

  // Fetch pending approvals
  useEffect(() => {
    const fetchPendingApprovals = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          "https://api.uniums.com/api/student/student-pending-leave",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const notifications = response.data?.notifications || [];
        setPendingApprovals(notifications.length);
      } catch (error) {
        console.error("Error fetching pending approvals:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPendingApprovals();
  }, []);

  if (loading) {
    return <Loader />;
  }

  // Data for Doughnut Chart (Current Month's Attendance)
  const pieData = {
    labels: ["Present", "Absent"],
    datasets: [
      {
        data: [attendanceData.present, attendanceData.absent],
        backgroundColor: ["#4CAF50", "#FF9800"], // Green for present, Orange for absent
        borderColor: ["#4CAF50", "#FF9800"],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = attendanceData.total;
            const percentage = Math.round((value / total) * 100) || 0;
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  // Data for Bar Chart (Yearly Attendance)
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const barData = {
    labels: months,
    datasets: [
      {
        label: "Present",
        data: yearlyAttendanceData.map((data) => data.present),
        backgroundColor: "#4CAF50", // Green
      },
      {
        label: "Absent",
        data: yearlyAttendanceData.map((data) => data.absent),
        backgroundColor: "#FF9800", // Orange
      },
    ],
  };

  const barOptions = {
    responsive: true,
    scales: {
      x: { stacked: false },
      y: {
        beginAtZero: true,
        stacked: false,
        title: {
          display: true,
          text: "Number of Classes",
        },
      },
    },
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.raw || 0;
            return `${label}: ${value}`;
          },
        },
      },
    },
  };

  // Month Selector Options
  const monthOptions = Array.from({ length: 12 }, (_, index) => {
    const month = new Date(0, index).toLocaleString("default", {
      month: "long",
    });
    return { value: index, label: month };
  });

  const handleMonthChange = (event) => {
    setSelectedMonth(Number(event.target.value));
  };

  return (
    <div className="faculty-dashboard">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="marquee-container">
        <div className="marquee">
          Welcome to UNIUMS {studentData.name} | Current Time: {formattedTime}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="card-container1">
        <div className="card_f">
          <img src={teacher} className="faculty_img_card" alt="teacher" />
          <div className="card_content">
            <h3>Total Class</h3>
            <p>{studentData.classCount}</p>
          </div>
        </div>
        <div className="card_f">
          <img src={books} className="faculty_img_card" alt="books" />
          <div className="card_content">
            <h3>Subjects Assigned</h3>
            <p>{studentData.subjectCount}</p>
          </div>
        </div>
        <div className="card_f">
          <img src={expired} className="faculty_img_card" alt="expired" />
          <div className="card_content">
            <h3>Pending Approvals</h3>
            <p>{pendingApprovals}</p>
          </div>
        </div>
      </div>

      {/* Month Filter */}
      <div className="month-filter">
        <label>Select Month: </label>
        <select onChange={handleMonthChange} value={selectedMonth}>
          {monthOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Charts Section */}
      {monthLoading ? (
        <div
          className="loading-container"
          style={{ textAlign: "center", margin: "40px 0" }}
        >
          <Loader />
        </div>
      ) : (
        <>
          <div className="charts_container">
            <div className="chart doughnut-chart1">
              <h3>
                Monthly Attendance Overview: {monthOptions[selectedMonth].label}
              </h3>
              {attendanceData.total > 0 ? (
                <Doughnut data={pieData} options={pieOptions} />
              ) : (
                <div className="no-data-message">
                  No attendance data for this month
                </div>
              )}
            </div>
            <div className="chart bar-chart1">
              <h3>Yearly Attendance Breakdown</h3>
              <Bar data={barData} options={barOptions} />
            </div>
          </div>

          {/* Attendance Table */}
          <div className="attendance-table-container">
  <h3 className="text_cls">
    Subject-wise Attendance for {monthOptions[selectedMonth].label}
  </h3>
  <table className="attendance-table">
    <thead>
      <tr>
        <th>Subject</th>
        <th>Present</th>
        <th>Absent</th>
        <th>Attendance (%)</th>
      </tr>
    </thead>
    <tbody>
      {subjectAttendanceData.length > 0 ? (
        <>
          {subjectAttendanceData.map((data, index) => (
            <tr key={index}>
              <td>{data.subject}</td>
              <td>{data.present}</td>
              <td>{data.absent}</td>
              <td>{data.attendancePercentage}%</td>
            </tr>
          ))}
          <tr className="overall-row" style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
            <td>Overall</td>
            <td>{attendanceData.present}</td>
            <td>{attendanceData.absent}</td>
            <td>
              {attendanceData.total > 0 
                ? Math.round((attendanceData.present / attendanceData.total) * 100) 
                : 0}%
            </td>
          </tr>
        </>
      ) : (
        <tr>
          <td
            colSpan="4"
            style={{ textAlign: "center" }}
            className="error"
          >
            No attendance data available for this month. Please check
            back later.
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>
        </>
      )}

      {/* Announcements */}
      <div className="announcement-section">
        <h3>Announcements</h3>
        <ul>
        {announcements && announcements.length > 0 ? (
            announcements.map((announcement, index) => (
              <li key={index} className="announcement-card-list">
                <h4>{announcement.title}</h4>
                <span>
                  <strong>Date:</strong>{" "}
                  {new Date(announcement.date).toLocaleDateString()} |
                  <strong>Time:</strong> {announcement.time}
                </span>
                <p className="announcement-p">{announcement.description}</p>

                <p>
                  <strong>Audience:</strong> {announcement.audience}
                </p>
                {announcement.location && (
                  <p>
                    <strong>Location:</strong> {announcement.location}
                  </p>
                )}
                {announcement.attachment && (
                  <p>
                    <a
                      href={announcement.attachment}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Attachment
                    </a>
                  </p>
                )}
              </li>
            ))
          ) : (
            <li>No announcements available at the moment.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default Home;
